import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Mail as MailIcon, Plus, RefreshCw, Search, Users, X as XIcon,
} from "lucide-react"
import { toast } from "@/components/portal/Toast"

/**
 * Die HERO-Karte im Mail-Detail: Kontakt-Infozeile, Verknuepfen und Loesen,
 * und die Projekte des verknuepften Kontakts.
 *
 * Aus pages/EmailsList.tsx herausgeloest (Schritt G).
 */

export function ContactInfoRow({ contact }: {
  contact: {
    id?: number; first_name?: string; last_name?: string; email?: string;
    phone?: string; phone_mobile?: string; phone_home?: string;
    address?: string; street?: string; zipcode?: string; city?: string;
  }
}) {
  const [hover, setHover] = useState(false)
  const phone = contact.phone_mobile || contact.phone_home || contact.phone || ""
  const hasInfo = contact.email || phone || contact.address
  if (!hasInfo) return null
  return (
    <div
      style={{ position: "relative", display: "inline-block", marginBottom: 8 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 12, flexWrap: "wrap" }}>
        {contact.email && (
          <a href={`mailto:${contact.email}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "inherit" }}>
            <MailIcon className="w-3 h-3" /> {contact.email}
          </a>
        )}
        {phone && (
          <a href={`tel:${phone}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "inherit" }}>
            <PhoneIcon /> {phone}
          </a>
        )}
        {contact.address && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <MapPinIcon /> {contact.address}
          </span>
        )}
      </div>
      {hover && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20,
            background: "var(--bg-card)", border: "1px solid var(--border-color)",
            borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
            padding: "10px 12px", minWidth: 260, maxWidth: 380,
            fontSize: 12, color: "var(--text)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {`${contact.first_name || ""} ${contact.last_name || ""}`.trim() || `Kontakt #${contact.id}`}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 10px", fontSize: 12 }}>
            {contact.email && (<>
              <span style={{ color: "var(--text-muted)" }}>E-Mail</span>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </>)}
            {contact.phone_mobile && (<>
              <span style={{ color: "var(--text-muted)" }}>Mobil</span>
              <a href={`tel:${contact.phone_mobile}`}>{contact.phone_mobile}</a>
            </>)}
            {contact.phone_home && (<>
              <span style={{ color: "var(--text-muted)" }}>Festnetz</span>
              <a href={`tel:${contact.phone_home}`}>{contact.phone_home}</a>
            </>)}
            {(contact.street || contact.zipcode || contact.city) && (<>
              <span style={{ color: "var(--text-muted)" }}>Adresse</span>
              <span>
                {contact.street && <div>{contact.street}</div>}
                {(contact.zipcode || contact.city) && <div>{[contact.zipcode, contact.city].filter(Boolean).join(" ")}</div>}
              </span>
            </>)}
            <span style={{ color: "var(--text-muted)" }}>ID</span>
            <span style={{ fontFamily: "monospace", color: "var(--text-muted)" }}>#{contact.id}</span>
          </div>
        </div>
      )}
    </div>
  )
}
function PhoneIcon() {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
}
function MapPinIcon() {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></svg>
}

export function HeroLinkActions({
  emailId, hasProject, locked, onOpenSearch, onAddProject,
}: {
  emailId: number
  hasProject: boolean
  locked: boolean
  onOpenSearch: () => void
  onAddProject?: () => void
}) {
  const qc = useQueryClient()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
    qc.invalidateQueries({ queryKey: ["emails"] })
    qc.invalidateQueries({ queryKey: ["email-hero-projects", emailId] })
  }
  const post = async (path: string, label: string) => {
    // path = "/clear-project" → {project_id: null}, "/clear-hero-ids" → {contact_id: null, project_id: null}
    try {
      const body = path === "/clear-project"
        ? { project_id: null }
        : { contact_id: null, project_id: null }
      const r = await fetch(`/api/v1/emails/${emailId}/hero-link`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`)
      toast(`✓ ${label}`)
      invalidate()
    } catch (e) {
      toast(`${label} fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`, "error")
    }
  }
  const unlock = async () => {
    // v1 hero-link PATCH {locked: false}
    try {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-link`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locked: false }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`)
      toast("✓ Auto-Suche reaktiviert")
      invalidate()
    } catch (e) {
      toast("Entsperren fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    }
  }

  return (
    <div style={{
      marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border-color)",
      display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
    }}>
      <button type="button" className="ev2-hero-ibtn"
        onClick={onOpenSearch}
        title="Anderen HERO-Kontakt suchen + verknüpfen">
        <Users className="w-3.5 h-3.5" /> Kontakt ändern
      </button>
      {hasProject && (
        <button type="button" className="ev2-hero-ibtn"
          onClick={onOpenSearch}
          title="Anderes Projekt wählen">
          <Search className="w-3.5 h-3.5" /> Projekt wechseln
        </button>
      )}
      {onAddProject && (
        <button type="button" className="ev2-hero-ibtn"
          onClick={onAddProject}
          title="Neues Projekt fuer diesen Kontakt anlegen">
          <Plus className="w-3.5 h-3.5" /> Projekt anlegen
        </button>
      )}
      {locked && (
        <button type="button" className="ev2-hero-ibtn"
          onClick={unlock}
          title="Auto-Suche wieder aktivieren">
          <RefreshCw className="w-3.5 h-3.5" /> Entsperren
        </button>
      )}
      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        {hasProject && (
          <button type="button" className="ev2-hero-ibtn"
            onClick={() => post("/clear-project", "Projekt entfernt")}
            title="Nur Projekt entfernen, Kontakt behalten"
            style={{ color: "var(--text-muted)" }}>
            Projekt entfernen
          </button>
        )}
        <button type="button" className="ev2-hero-ibtn"
          style={{ color: "#dc2626" }}
          onClick={() => { if (confirm("Kontakt + Projekt komplett entfernen?")) post("/clear-hero-ids", "Verknüpfung aufgehoben") }}
          title="Kontakt + Projekt komplett entfernen">
          <XIcon className="w-3.5 h-3.5" /> Verknüpfung lösen
        </button>
      </div>
    </div>
  )
}

export function HeroContactProjects({
  emailId, currentProjectId, tenantSlug,
}: {
  emailId: number
  currentProjectId?: number | string | null
  tenantSlug?: string
}) {
  const qc = useQueryClient()
  type ProjectHit = {
    id: number; name: string; title?: string; code?: string;
    status?: string; status_code?: string; step_id?: number; type_id?: number
  }
  type Step = { id: number; name: string; status_code?: string; type_id: number }
  const q = useQuery<{
    contact?: {
      id: number; first_name?: string; last_name?: string; email?: string;
      phone?: string; phone_mobile?: string; phone_home?: string;
      address?: string; street?: string; zipcode?: string; city?: string;
    } | null
    projects: ProjectHit[]
    steps?: Step[]
    locked?: boolean
    error?: string
  }>({
    queryKey: ["email-hero-projects", emailId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/emails/${emailId}/hero-projects`, { credentials: "include" })
      if (!res.ok) return { projects: [] }
      return res.json()
    },
    staleTime: 1000 * 30,
  })

  const assign = useMutation({
    mutationFn: async (projectId: number) => {
      const res = await fetch(`/api/v1/emails/${emailId}/hero-link`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ project_id: projectId }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`)
      return j
    },
    onSuccess: () => {
      toast("✓ Projekt verknüpft")
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
    onError: (err) => toast("Verknüpfen fehlgeschlagen: " + (err instanceof Error ? err.message : String(err)), "error"),
  })

  const changeStatus = useMutation({
    mutationFn: async ({ projectId, stepId }: { projectId: number; stepId: number }) => {
      const res = await fetch(`/api/v1/emails/${emailId}/hero-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ project_id: projectId, step_id: stepId }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`)
      return j
    },
    onSuccess: () => {
      toast("✓ Projekt-Status geändert")
      qc.invalidateQueries({ queryKey: ["email-hero-projects", emailId] })
    },
    onError: (err) => toast("Status ändern fehlgeschlagen: " + (err instanceof Error ? err.message : String(err)), "error"),
  })

  if (q.isLoading) {
    return <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "6px 0" }}>Lädt Projekte…</div>
  }
  const projects = q.data?.projects || []
  const contact = q.data?.contact
  const steps = q.data?.steps || []

  // Kein Kontakt gefunden, keine Projekte → explizit NICHTS rendern, User sieht nur
  // die Aktions-Buttons oben (Kontakt anlegen / Kontakt suchen).
  if (!contact && !projects.length) return null

  // Pipeline-spezifischen Status-Namen mappen.
  // WICHTIG: Match ueber step_id (Tenant-spezifischer Pipeline-Step), NICHT
  // ueber status_code — bei Sanitec teilen mehrere Steps denselben status_code
  // (z.B. "Umsetzungstermin vereinbaren" + "Umsetzungsbeginn" + "Umsetzung
  // durchfuehren" alle sc=1101). Match auf status_code wuerde immer den ersten
  // Treffer liefern, unabhaengig vom echten Status. Fallback fuer alte
  // Frontend-Daten ohne step_id: status_code-Match wie frueher.
  const getPipelineStatus = (p: ProjectHit) => {
    if (p.step_id) {
      const match = steps.find((s) => Number(s.id) === Number(p.step_id))
      if (match) return match.name
    }
    if (!p.status_code || !p.type_id) return p.status
    const match = steps.find((s) => String(s.type_id) === String(p.type_id) && String(s.status_code) === String(p.status_code))
    return match?.name || p.status
  }

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border-color)" }}>
      {/* Kontakt-Info (Email/Tel/Adresse) direkt — Name+ID stehen schon oben in der Verknuepft-Zeile */}
      {contact && <ContactInfoRow contact={contact} />}
      {projects.length > 0 && (
        <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, color: "var(--text-muted)", textTransform: "uppercase", margin: "4px 0 6px" }}>
          {projects.length} Projekt{projects.length === 1 ? "" : "e"}
          {q.data?.locked && <span style={{ marginLeft: 8, background: "var(--secondary-bg)", color: "var(--text-muted)", padding: "1px 6px", borderRadius: 8, fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>gesperrt</span>}
        </div>
      )}
      {projects.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "6px 0", fontStyle: "italic" }}>
          Keine Projekte für diesen Kontakt gefunden.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {projects.map((p) => {
            const isCurrent = String(p.id) === String(currentProjectId || "")
            const displayStatus = getPipelineStatus(p)
            return (
              <div
                key={p.id}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 8px", borderRadius: 6,
                  background: isCurrent ? "rgba(16,185,129,0.08)" : "#f8fafc",
                  border: isCurrent ? "1px solid #10b981" : "1px solid transparent",
                  fontSize: 13, flexWrap: "wrap",
                }}
              >
                {/* Status-Dropdown: direkte Pipeline-Status-Aenderung inline */}
                <select
                  value={p.step_id || ""}
                  onChange={(ev) => {
                    const nextId = Number(ev.target.value)
                    if (nextId && nextId !== p.step_id) {
                      changeStatus.mutate({ projectId: p.id, stepId: nextId })
                    }
                  }}
                  disabled={changeStatus.isPending}
                  title="Pipeline-Status ändern"
                  style={{
                    background: "#6366f1", color: "#fff",
                    padding: "2px 6px", borderRadius: 10,
                    fontSize: 12, fontWeight: 600, border: "none",
                    cursor: "pointer", maxWidth: "100%",
                  }}
                >
                  <option value="">{displayStatus || "— Status —"}</option>
                  {steps
                    .filter((s) => String(s.type_id) === String(p.type_id))
                    .map((s) => (
                      <option key={s.id} value={s.id} style={{ background: "#fff", color: "#0f172a" }}>
                        {s.name}
                      </option>
                    ))}
                </select>
                {p.code && (
                  <span style={{
                    background: "#e8f0fe", color: "#1a73e8",
                    padding: "1px 5px", borderRadius: 3,
                    fontSize: 12, fontWeight: 600, letterSpacing: 0.3,
                  }}>
                    {p.code}
                  </span>
                )}
                <span
                  title={p.name || `Projekt ${p.id}`}
                  style={{
                    // `flex: 1` allein liess den Namen bis auf "H…" schrumpfen:
                    // mit Basis 0 passt er rechnerisch immer noch in die Zeile.
                    // Mit 140px Basis bricht er stattdessen um — in der 300px
                    // breiten Metaspalte ist das der Normalfall.
                    flex: "1 1 140px", fontWeight: 500, minWidth: 0, maxWidth: "100%",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {p.name || `Projekt ${p.id}`}
                </span>
                {isCurrent && (
                  <span style={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>✓</span>
                )}
                <a
                  href={`/admin/open-hero/project/${p.id}${tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ""}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 12, color: "#3730a3" }}
                >
                  ↗ HERO
                </a>
                {!isCurrent && (
                  <button
                    type="button"
                    onClick={() => assign.mutate(p.id)}
                    disabled={assign.isPending}
                    style={{
                      fontSize: 12, padding: "3px 8px",
                      background: "#eef2ff", color: "#3730a3",
                      border: "1px solid #c7d2fe", borderRadius: 4,
                      cursor: "pointer",
                    }}
                  >
                    Zuordnen
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** Extrahiert Kontakt-Prefill aus E-Mail (Claude-Analyse in contact_data_json
 *   + Fallback auf Sender-Header fuer Name+E-Mail). */
